import React from 'react'
import Link from '@/components/link-component'
import { cx } from '@/utils/strings'
import { type TypeComponentIcon } from '@/types/ctf'

import ArrowRight from '@/icons/bwi/angle-right.svg'

export const buttonStyles = {
  btnArrow: 'Arrow Link',
  btnOutlined: 'Button: Outlined',
  btnOutlinedSecondary: 'Button: Outlined Secondary',
  btnSolid: 'Button: Solid',
  btnSolidSecondary: 'Button: Solid Secondary',
  btnOutlinedSolidBg: 'Button: Outlined Solid Bg',
  btnOutlinedTertiary: 'Button: Solid Tertiary',
  btnGhost: 'Button: Ghost',
  btnPlain: 'Plain Link',
  btnText: 'Text Link',
}

const {
  btnSolid,
  btnSolidSecondary,
  btnOutlined,
  btnOutlinedSecondary,
  btnOutlinedTertiary,
  btnOutlinedSolidBg,
  btnGhost,
  btnArrow,
  btnPlain,
  btnText,
} = buttonStyles

export type DisplayType = 'Inline' | 'Block'
export type AlignType = 'Left' | 'Center'
export type SizeType = 'Smaller' | 'Base' | 'Larger'
export type VariantType =
  | typeof btnArrow
  | typeof btnOutlined
  | typeof btnSolid
  | typeof btnOutlinedSecondary
  | typeof btnOutlinedTertiary
  | typeof btnSolidSecondary
  | typeof btnGhost
  | typeof btnPlain
  | typeof btnText

export type CtaLinkProps = {
  align?: AlignType // Used in component logic
  asButton?: boolean // Used to determine rendering
  bold?: boolean // Used in styling logic
  children?: any // Used to render content
  className?: string // Used for styling
  css?: string // Used in buttonStyle
  disabled?: boolean // Disabled button style
  display?: DisplayType // Used in styling
  icon?: TypeComponentIcon<'WITHOUT_UNRESOLVABLE_LINKS'> | string // Used for icon rendering
  iconPosition?: string // Used for icon positioning
  onClick?: React.MouseEventHandler<HTMLAnchorElement> // Would be spread in ...other
  plausibleEventName?: string // Used for analytics class
  scrollToOffset?: number // Would be spread in ...other
  size?: SizeType // Used for styling
  style?: VariantType // Used for styling variations
  to?: string // Used for link destination
  [key: string]: any // Catch-all for other props
}

export default function CtaLink({
  align = 'Left',
  asButton = false,
  bold = false,
  children,
  css,
  disabled,
  display = 'Inline',
  icon = undefined,
  iconPosition = 'Left',
  className: _className,
  plausibleEventName,
  size = 'Base',
  style = btnArrow,
  to,
  ...other
}: CtaLinkProps) {
  // remove props we don't want to pass to the button or link
  delete other?.link
  delete other?.internalName
  delete other?.entryId

  /**
   * Focus styles for the button
   *
   * Note: Specify the bg & border color with `focus:before:` where you apply this
   */
  const btnFocusStyle = [].join(' ')

  const textFocusStyle = [
    'focus:outline-hidden focus:outline-hidden focus:border-b focus:border-secondary-700 dark:focus:border-primaryBlue',
    '[&_.arrow]:transform-gpu hover:[&_.arrow]:translate-x-1 group-hover:[&_.arrow]:translate-x-1',
  ].join(' ')

  let className = _className || ''
  if (plausibleEventName?.trim()) {
    className += ` ${plausibleEventName}`
  }

  const buttonStyle = cx(
    'font-medium z-0',
    size === 'Smaller' && 'text-sm',
    size === 'Base' && 'text-base',
    size === 'Larger' && 'text-lg',
    align === 'Center' && 'mx-auto',
    display === 'Inline' && 'inline-block',
    display === 'Block' && 'table',
    !disabled &&
      (btnArrow === style || btnPlain === style) &&
      `border-b-1 border-b-transparent text-primaryBlue hover:text-primary-700 dark:hover:text-primaryBlue group-hover:text-primary-700 dark:group-hover:text-primaryBlue ${textFocusStyle}`,
    style !== btnArrow && style !== btnPlain && style !== btnText && 'rounded-full text-center',
    size === 'Smaller' && style !== btnArrow && style !== btnPlain && style !== btnText && 'px-3 py-2',
    size === 'Base' && style !== btnArrow && style !== btnPlain && style !== btnText && 'px-5 py-3',
    size === 'Larger' && style !== btnArrow && style !== btnPlain && style !== btnText && 'px-9 py-4',
    !disabled && btnPlain === style && 'underline text-primaryBlue',
    !disabled && btnSolid === style && `cta-solid-primary ${btnFocusStyle}`,
    !disabled && btnSolidSecondary === style && `cta-solid-secondary ${btnFocusStyle}`,
    !disabled && btnOutlined === style && `cta-outline-primary`,
    !disabled && btnOutlinedSecondary === style && `cta-outline-secondary`,
    !disabled && btnOutlinedTertiary === style && `cta-outline-tertiary`,
    !disabled && btnOutlinedSolidBg === style && `cta-solid-secondary-w-outline`,

    // Ghost button styling - transparent initially, solid on hover
    !disabled &&
      btnGhost === style &&
      `border-2 border-transparent text-primary-700 bg-transparent hover:bg-bw-teal-light-highlight group-hover:bg-bw-teal-light-highlight hover:text-black group-hover:text-black transition-color duration-200 dark:text-secondary-200 dark:hover:bg-white dark:group-hover:bg-white dark:hover:text-primary-700 dark:group-hover:text-primary-700`,

    btnText === style && 'no-underline',
    css,
    className.trim(),
    disabled && 'cursor-disabled focus:outline-hidden',
    disabled && (btnArrow === style || btnPlain === style) && 'text-muted',

    disabled &&
      btnSolid === style &&
      'bg-secondary-300 dark:bg-secondary-600 border-2 border-secondary-300 dark:border-secondary-600 text-muted',
    disabled &&
      btnSolidSecondary === style &&
      'bg-secondary-300 dark:bg-secondary-600 border-2 border-secondary-300 dark:border-secondary-600 text-muted',
    disabled &&
      btnOutlinedSolidBg === style &&
      'bg-secondary-300 dark:bg-secondary-600 border-2 border-secondary-300 dark:border-secondary-600 text-muted',
    disabled && btnOutlined === style && 'border-2 border-secondary-300 hover:border-secondary-300 text-muted',
    disabled &&
      btnGhost === style &&
      'border-2 border-transparent bg-transparent text-muted hover:bg-transparent hover:text-muted'
  )

  const renderBtnText = () => {
    const unicode = typeof icon === 'object' ? icon?.fields?.unicode : icon
    return (
      <span className={cx('flex items-center', bold && 'font-bold')}>
        {unicode && iconPosition === 'Left' && (
          <span className="font-bwi mr-2 text-base">{String.fromCodePoint(parseInt(unicode, 16))}</span>
        )}
        {children}

        {btnArrow === style && <ArrowRight className="arrow transition-transform duration-250 delay-75" />}
        {unicode && iconPosition === 'Right' && (
          <span className="font-bwi ml-2 text-base">{String.fromCodePoint(parseInt(unicode, 16))}</span>
        )}
      </span>
    )
  }

  return asButton ? (
    <button disabled={disabled} type="button" data-testid="cta-link" className={buttonStyle} {...other}>
      {renderBtnText()}
    </button>
  ) : (
    <Link to={to} className={buttonStyle} data-testid="cta-link" {...other}>
      {renderBtnText()}
    </Link>
  )
}
